import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  getExchangeRates, 
  getLatestExchangeRate,
  createExchangeRate,
  deleteExchangeRate
} from '../api/client';
import { formatDate } from '../utils/formatters';

const ExchangeRate = () => {
  const [exchangeRates, setExchangeRates] = useState([]);
  const [latestRate, setLatestRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date(),
    usd_to_ves_rate: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rates, latest] = await Promise.all([
        getExchangeRates(30),
        getLatestExchangeRate()
      ]);
      setExchangeRates(rates);
      setLatestRate(latest);
      setError(null);
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
      setError('Error al cargar las tasas de cambio');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenModal = () => {
    setFormData({
      date: new Date(),
      usd_to_ves_rate: latestRate?.usd_to_ves_rate?.toString() || '',
      notes: ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      date: new Date(),
      usd_to_ves_rate: '',
      notes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const rateData = {
        date: formData.date.toISOString().split('T')[0],
        usd_to_ves_rate: parseFloat(formData.usd_to_ves_rate),
        notes: formData.notes
      };

      await createExchangeRate(rateData);
      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Error saving exchange rate:', err);
      setError('Error al guardar la tasa de cambio: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta tasa de cambio?')) {
      return;
    }

    try {
      await deleteExchangeRate(rateId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting exchange rate:', err);
      setError('Error al eliminar la tasa de cambio');
    }
  };

  const formatCurrency = (amount, currency = 'VES') => {
    if (currency === 'VES') {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency',
        currency: 'VES',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando tasas de cambio...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💱 Tasa de Cambio USD/VES</h1>
          <p className="text-gray-600 mt-1">
            Gestión de tasas de cambio para conversión automática de monedas
          </p>
        </div>
        <button onClick={handleOpenModal} className="btn-primary">
          📊 Nueva Tasa
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Current Exchange Rate Card */}
      {latestRate && (
        <div className="card bg-gradient-to-r from-blue-50 to-green-50 border-l-4 border-blue-500">
          <div className="card-header">
            <h3 className="card-title">📈 Tasa Actual</h3>
            <span className="text-sm text-gray-600">
              Actualizada: {formatDate(latestRate.date)}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                Bs. {latestRate.usd_to_ves_rate?.toFixed(2)}
              </div>
              <div className="text-sm text-gray-600">por cada $1 USD</div>
            </div>
            <div className="text-center">
              <div className="text-xl text-gray-700">
                $100 USD = {formatCurrency(latestRate.usd_to_ves_rate * 100, 'VES')}
              </div>
              <div className="text-sm text-gray-600">Ejemplo de conversión</div>
            </div>
            <div className="text-center">
              <div className="text-lg text-gray-600">
                {latestRate.notes || 'Sin notas adicionales'}
              </div>
              <div className="text-sm text-gray-500">Notas</div>
            </div>
          </div>
        </div>
      )}

      {/* Exchange Rates History */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📊 Historial de Tasas</h3>
          <button onClick={fetchData} className="btn-outline text-sm">
            🔄 Actualizar
          </button>
        </div>

        {exchangeRates.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💱</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay tasas de cambio registradas
            </h3>
            <p className="text-gray-600 mb-4">
              Registra tu primera tasa de cambio para comenzar a usar el sistema de doble moneda.
            </p>
            <button onClick={handleOpenModal} className="btn-primary">
              📊 Registrar Primera Tasa
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tasa (VES por USD)</th>
                  <th>Ejemplo ($100 USD)</th>
                  <th>Notas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {exchangeRates.map((rate) => (
                  <tr key={rate.id}>
                    <td>
                      <span className="font-medium">
                        {formatDate(rate.date)}
                      </span>
                    </td>
                    <td>
                      <span className="text-lg font-bold text-blue-600">
                        Bs. {rate.usd_to_ves_rate?.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span className="text-green-600 font-medium">
                        {formatCurrency(rate.usd_to_ves_rate * 100, 'VES')}
                      </span>
                    </td>
                    <td>
                      <span className="text-sm text-gray-600">
                        {rate.notes || '-'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(rate.id)}
                        className="btn-outline-danger text-sm"
                        disabled={rate.id === 'default'}
                      >
                        🗑️ Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Exchange Rate Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">💱 Registrar Tasa de Cambio</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">ℹ️ Información</h4>
                  <p className="text-sm text-blue-700">
                    Esta tasa se usará para convertir automáticamente todas las cifras entre USD y VES en toda la aplicación.
                    Si ya existe una tasa para esta fecha, será actualizada.
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Fecha *</label>
                  <DatePicker
                    selected={formData.date}
                    onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                    className="form-input"
                    dateFormat="dd/MM/yyyy"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Tasa USD a VES *</label>
                  <input
                    type="number"
                    value={formData.usd_to_ves_rate}
                    onChange={(e) => setFormData(prev => ({ ...prev, usd_to_ves_rate: e.target.value }))}
                    className="form-input"
                    placeholder="Ej: 36.50"
                    step="0.01"
                    min="0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Cuántos bolívares equivalen a 1 dólar
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="form-textarea"
                    rows="3"
                    placeholder="Ej: Tasa BCV oficial, tasa paralela, etc."
                  />
                </div>

                {formData.usd_to_ves_rate && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">📊 Preview de Conversión</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-green-700">$1 USD =</span>
                        <span className="font-bold text-green-900 ml-1">
                          Bs. {parseFloat(formData.usd_to_ves_rate || 0).toFixed(2)}
                        </span>
                      </div>
                      <div>
                        <span className="text-green-700">$100 USD =</span>
                        <span className="font-bold text-green-900 ml-1">
                          Bs. {(parseFloat(formData.usd_to_ves_rate || 0) * 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      Guardando...
                    </span>
                  ) : (
                    'Guardar Tasa'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeRate;